import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { COUNTRIES_OF_ORIGIN, validateMobile } from '@/lib/countries';
import MobileInput from '@/components/marketplace/MobileInput';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Save, Pencil, X, ShieldCheck, ShieldAlert } from 'lucide-react';
import ProfilePhotoUploader from '@/components/marketplace/ProfilePhotoUploader';

const emptyForm = {
  name: '', mobile: '', company_name: '', company_description: '',
  gst_number: '', industrial_area: '', full_address: '',
  country: '', state: '', city: ''
};

const isIndia = (c) => String(c || '').trim().toLowerCase() === 'india';

// Simple view row used in read-only mode
const ViewRow = ({ label, value, testid, empty = '— Not provided —', className = '' }) => (
  <div className={className}>
    <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-medium'>{label}</div>
    <div className='text-sm mt-1 whitespace-pre-line' data-testid={testid}>
      {value ? value : <span className='text-muted-foreground italic'>{empty}</span>}
    </div>
  </div>
);

const CompanyProfile = () => {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(emptyForm);
  // Review.pdf #4: default to View Mode, single visible Edit button toggles
  // the entire page into edit mode. This prevents accidental edits/data loss.
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Snapshot of last-saved values for Cancel-to-restore
  const [snapshot, setSnapshot] = useState(emptyForm);

  // India-only State/City dropdown sources; non-India uses free-text.
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  useEffect(() => { api.get('/locations/states').then(r => setStates(r.data)).catch(() => setStates([])); }, []);
  useEffect(() => {
    if (isIndia(form.country) && form.state) {
      api.get('/locations/cities', { params: { state: form.state } }).then(r => setCities(r.data)).catch(() => setCities([]));
    } else setCities([]);
  }, [form.state, form.country]);

  const countryOptions = React.useMemo(() => {
    const c = String(form.country || '').trim();
    return c && !COUNTRIES_OF_ORIGIN.includes(c) ? [c, ...COUNTRIES_OF_ORIGIN] : COUNTRIES_OF_ORIGIN;
  }, [form.country]);

  useEffect(() => {
    if (user) {
      const next = {
        name: user.name || '',
        mobile: user.mobile || '',
        company_name: user.company_name || '',
        company_description: user.company_description || '',
        gst_number: user.gst_number || '',
        industrial_area: user.industrial_area || '',
        country: user.country || '',
        state: user.state || '',
        city: user.city || '',
        full_address: user.full_address || '',
      };
      setForm(next);
      setSnapshot(next);
    }
  }, [user]);

  const startEdit = () => {
    setSnapshot(form);
    setEditMode(true);
  };
  const cancelEdit = () => {
    setForm(snapshot);
    setEditMode(false);
  };

  const save = async () => {
    const mobileCheck = validateMobile(form.mobile);
    if (!mobileCheck.ok) { toast.error(mobileCheck.message); return; }
    setSaving(true);
    try {
      await api.patch('/auth/me', form);
      toast.success('Profile updated');
      await refresh();
      setSnapshot(form);
      setEditMode(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className='space-y-5 max-w-3xl' data-testid={editMode ? 'profile-edit-mode' : 'profile-view-mode'}>
      <div className='flex items-start justify-between gap-3 flex-wrap'>
        <div>
          <h1 className='text-2xl font-semibold tracking-tight'>Profile &amp; Company</h1>
          <p className='text-muted-foreground text-sm'>
            {editMode
              ? 'Update your personal and company details. Company details are private.'
              : 'Your personal and company details. Company info stays private and is shown only after buyers reveal contact.'}
          </p>
        </div>
        {!editMode ? (
          <Button onClick={startEdit} data-testid='profile-edit-btn'>
            <Pencil className='h-4 w-4 mr-1.5' /> Edit profile
          </Button>
        ) : (
          <div className='flex gap-2'>
            <Button variant='outline' onClick={cancelEdit} disabled={saving} data-testid='profile-cancel-btn'>
              <X className='h-4 w-4 mr-1.5' /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid='profile-save-btn'>
              <Save className='h-4 w-4 mr-1.5' /> {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </div>

      <Card className='p-6 space-y-4' data-testid='profile-photo-card'>
        <ProfilePhotoUploader user={user} onChange={refresh} />
      </Card>

      {/* Personal + Company block. In view mode, render read-only rows.
          In edit mode, render inputs identical to before. */}
      <Card className='p-6 space-y-4'>
        {editMode ? (
          <div className='grid md:grid-cols-2 gap-4'>
            <div><Label>Full name</Label><Input data-testid='profile-name' value={form.name} onChange={set('name')} /></div>
            <div><Label>Mobile</Label>
              <MobileInput
                value={form.mobile}
                country={form.country}
                onChange={(v) => setForm(f => ({ ...f, mobile: v }))}
                testid='profile-mobile'
              />
            </div>
            <div><Label>Company name</Label><Input data-testid='profile-company' value={form.company_name} onChange={set('company_name')} /></div>
            <div><Label>GST number</Label><Input value={form.gst_number} onChange={set('gst_number')} data-testid='profile-gst' /></div>
            <div><Label>Country</Label>
              <Select value={form.country || ''} onValueChange={(v) => setForm(f => ({ ...f, country: v, state: '', city: '' }))}>
                <SelectTrigger data-testid='profile-country'><SelectValue placeholder='Select country' /></SelectTrigger>
                <SelectContent className='max-h-72'>
                  {countryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isIndia(form.country) ? (
              <div><Label>State / Province / Region</Label>
                <Select value={form.state || ''} onValueChange={(v) => setForm(f => ({ ...f, state: v, city: '' }))}>
                  <SelectTrigger data-testid='profile-state'><SelectValue placeholder='Select' /></SelectTrigger>
                  <SelectContent>{states.map(s => <SelectItem key={s.state} value={s.state}>{s.state}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div><Label>State / Province / Region</Label><Input data-testid='profile-state' value={form.state} onChange={set('state')} placeholder='e.g. Guangdong' /></div>
            )}
            {isIndia(form.country) ? (
              <div><Label>City / Location</Label>
                <Select value={form.city || ''} onValueChange={(v) => setForm(f => ({ ...f, city: v }))} disabled={!form.state}>
                  <SelectTrigger data-testid='profile-city'><SelectValue placeholder='Select' /></SelectTrigger>
                  <SelectContent>{cities.map(c => <SelectItem key={c.id} value={c.city}>{c.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div><Label>City / Location</Label><Input data-testid='profile-city' value={form.city} onChange={set('city')} placeholder='e.g. Shenzhen' /></div>
            )}
            <div className='md:col-span-2'><Label>Industrial Area / Locality</Label><Input value={form.industrial_area} onChange={set('industrial_area')} data-testid='profile-industrial-area' /></div>
            <div className='md:col-span-2'><Label>Full address (private)</Label><Input value={form.full_address} onChange={set('full_address')} data-testid='profile-full-address' /></div>
            <div className='md:col-span-2'><Label>Company description</Label><Textarea rows={4} value={form.company_description} onChange={set('company_description')} data-testid='profile-company-desc' /></div>
          </div>
        ) : (
          <div className='grid md:grid-cols-2 gap-x-5 gap-y-4'>
            <ViewRow label='Full name' value={form.name} testid='profile-view-name' />
            <ViewRow label='Mobile' value={form.mobile} testid='profile-view-mobile' />
            <ViewRow label='Company name' value={form.company_name} testid='profile-view-company' />
            <ViewRow label='Country' value={form.country} testid='profile-view-country' />
            <ViewRow label='State / Province / Region' value={form.state} testid='profile-view-state' />
            <ViewRow label='City / Location' value={form.city} testid='profile-view-city' />
            <div>
              <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-medium'>GST number</div>
              <div className='text-sm mt-1 flex items-center gap-2'>
                {form.gst_number ? (
                  <>
                    <span data-testid='profile-view-gst'>{form.gst_number}</span>
                    {user?.gst_verified ? (
                      <Badge variant='outline' className='bg-emerald-500/10 border-emerald-500/30 text-emerald-700 text-[10px]'>
                        <ShieldCheck className='h-3 w-3 mr-1' /> Verified
                      </Badge>
                    ) : (
                      <Badge variant='outline' className='bg-amber-500/10 border-amber-500/30 text-amber-700 text-[10px]'>
                        <ShieldAlert className='h-3 w-3 mr-1' /> Not verified
                      </Badge>
                    )}
                  </>
                ) : (
                  <span className='text-muted-foreground italic'>— Not provided —</span>
                )}
              </div>
            </div>
            <ViewRow label='Industrial Area / Locality' value={form.industrial_area} testid='profile-view-industrial-area' className='md:col-span-2' />
            <ViewRow label='Full address (private)' value={form.full_address} testid='profile-view-full-address' className='md:col-span-2' />
            <ViewRow label='Company description' value={form.company_description} testid='profile-view-company-desc' className='md:col-span-2' />
          </div>
        )}
      </Card>

      {editMode && (
        <div className='flex justify-end'>
          <Button onClick={save} disabled={saving} data-testid='profile-save-bottom'>
            <Save className='h-4 w-4 mr-1.5' /> {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanyProfile;
